import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SET_API_DATA } from "../redux/constants";
import { ApiContainer } from "../utils/api";

const EventStatisticsContainer = () => {
  const dispatch = useDispatch();
  const { api } = ApiContainer();
  const { eventId, organizationId, locationId, departmentId } = useParams();
  const eventStatisticsData = useSelector(
    (state) => state.api.eventStatisticsData
  );
  const requiredAssessmentsData = useSelector(
    (state) => state.api.requiredAssessmentsData
  );
  const annualPhysicalsData = useSelector(
    (state) => state.api.annualPhysicalsData
  );
  const eventParticipationData = useSelector(
    (state) => state.api.eventParticipationData
  );
  const loaders = useSelector((state) => {
    return {
      eventStatisticsData: state.api?.loader?.eventStatisticsData,
      requiredAssessmentsData: state.api?.loader?.requiredAssessmentsData,
      annualPhysicalsData: state.api?.loader?.annualPhysicalsData,
      eventParticipationData: state.api?.loader?.eventParticipationData,
    };
  });

  const eventEndPoints = `organization/${organizationId}/locations/${locationId}/departments/${departmentId}/event-schedule/${eventId}`;

  const [mainFrequency, setMainFrequency] = useState("");
  const [localFilters, setLocalFilters] = useState({
    requiredAssessments: null,
    annualPhysicals: null,
    eventParticipation: null,
    eventParticipationAssessment: null,
  });

  const getAllStatistics = async (frequency) => {
    const response = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/event-statistics?${
        frequency ? `days=${frequency}` : ""
      }`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "eventStatisticsData",
    });

    if (response?.status) {
      dispatch({
        type: SET_API_DATA,
        payload: {
          eventStatisticsData: response?.data,
        },
      });
    }
  };

  const getRequiredAssessments = async (frequency, filter) => {
    const response = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/required-online-assessments-overview?${
        frequency ? `days=${frequency}` : ""
      }${filter ? `assessment=${filter}` : ""}`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "requiredAssessmentsData",
    });

    if (response?.status) {
      dispatch({
        type: SET_API_DATA,
        payload: {
          requiredAssessmentsData: response?.data,
        },
      });
    }
  };

  const getAnnualPhysicals = async (frequency, filter) => {
    const response = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/annual-physicals-overview?${
        frequency ? `days=${frequency}` : ""
      }${filter ? `assessment=${filter}` : ""}`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "annualPhysicalsData",
    });

    if (response?.status) {
      dispatch({
        type: SET_API_DATA,
        payload: {
          annualPhysicalsData: response?.data,
        },
      });
    }
  };

  const getEventParticipation = async (frequency, filters = {}) => {
    const params = new URLSearchParams();
    if (frequency) {
      params.append("days", frequency);
    }
    if (filters.filter) {
      params.append("event_participation_over_time", filters.filter);
    }

    if (filters.assessment_filter) {
      params.append("assessment", filters.assessment_filter);
    }

    const response = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/event-participation-over-time?${params.toString()}`,
      showToastMessage: false,
      urlencoded: false,
      parent: "eventParticipationData",
      needLoader: true,
    });

    if (response?.status) {
      dispatch({
        type: SET_API_DATA,
        payload: {
          eventParticipationData: response?.data,
        },
      });
    }
  };

  const handleMainFrequencyChange = (value) => {
    setMainFrequency(value);
    getAllStatistics(value);
    getRequiredAssessments(value, localFilters.requiredAssessments);
    getAnnualPhysicals(value, localFilters.annualPhysicals);

    const EventParticipationFilter = localFilters.eventParticipation;
    const { eventParticipationAssessment } = localFilters;

    if (
      ["assessment_completed", "results_viewed"].includes(
        EventParticipationFilter
      )
    ) {
      getEventParticipation(value, {
        filter: EventParticipationFilter,
        assessment_filter: eventParticipationAssessment,
      });
    } else {
      getEventParticipation(value, { filter: EventParticipationFilter });
    }
  };

  const handleChartFilterChange = (chartKey, value) => {
    setLocalFilters((prev) => ({ ...prev, [chartKey]: value }));

    if (chartKey === "eventParticipation") {
      const isDoubleFilter = [
        "assessment_completed",
        "results_viewed",
      ].includes(value);
      if (!isDoubleFilter) {
        setLocalFilters((prev) => ({
          ...prev,
          eventParticipation: value,
          eventParticipationAssessment: null,
        }));
        getEventParticipation(mainFrequency, { filter: value });
      }
    }

    if (chartKey === "eventParticipationAssessment") {
      const filter = localFilters.eventParticipation;
      getEventParticipation(mainFrequency, {
        filter,
        assessment_filter: value,
      });
    }

    switch (chartKey) {
      case "requiredAssessments":
        getRequiredAssessments(mainFrequency, value);
        break;
      case "annualPhysicals":
        getAnnualPhysicals(mainFrequency, value);
        break;
      default:
        break;
    }
  };

  const handleDownloadReport = async (reportType) => {
    const frequency = mainFrequency;
    const assessmentFilter = {
      required_online_assessments_overview_filter:
        localFilters.requiredAssessments,
      annual_physicals_overview_filter: localFilters.annualPhysicals,
      event_participation_over_time_filter: localFilters.eventParticipation,
    }[reportType];

    const params = new URLSearchParams();

    if (frequency) {
      params.append("days", frequency);
    }
    if (reportType) {
      params.append("report_type", reportType);
    }
    if (assessmentFilter) {
      params.append("assessment", assessmentFilter);
    }
    const data = await api({
      method: "GET",
      endPoint: `${eventEndPoints}/download-report?${params.toString()}`,
      showToastMessage: true,
      urlencoded: false,
      needLoader: true,
      parent: "downloadCSVFile",
      needFullResponse: true,
    });

    if (
      data?.status &&
      data?.data?.data?.csv &&
      data?.data?.message === "CSV generated successfully"
    ) {
      const csvContent = data?.data?.data?.csv;
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const fileName = `Event_Schedule_${reportType}.csv`;
      const link = document.createElement("a");
      const fileURL = URL.createObjectURL(blob);
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    }
  };

  useEffect(() => {
    const init = async () => {
      await getAllStatistics(mainFrequency);
      await getRequiredAssessments(
        mainFrequency,
        localFilters.requiredAssessments
      );
      await getAnnualPhysicals(mainFrequency, localFilters.annualPhysicals);
      await getEventParticipation(mainFrequency, {});
    };
    init();
  }, [eventId]);

  return {
    eventStatisticsData,
    requiredAssessmentsData,
    annualPhysicalsData,
    eventParticipationData,
    handleChartFilterChange,
    mainFrequency,
    handleMainFrequencyChange,
    localFilters,
    loaders,
    handleDownloadReport,
  };
};

export default EventStatisticsContainer;
